export type ApiErrorCode =
  | 'VALIDATION_ERROR'
  | 'UNAUTHORIZED'
  | 'FORBIDDEN'
  | 'NOT_FOUND'
  | 'CONFLICT'
  | 'RATE_LIMITED'
  | 'INTERNAL_ERROR'
  | 'TERMS_ACCEPTANCE_REQUIRED'
  | 'PROVIDER_NOT_CONFIGURED'
  | 'CHAT_READ_ONLY'
  | 'NETWORK_ERROR'
  | 'TIMEOUT'
  | 'OFFLINE'
  | 'ABORTED'
  | 'UNKNOWN'

export interface ApiErrorEnvelope {
  error: {
    code: string
    message: string
    details?: unknown
    messageKey?: string
  }
}

export interface ApiErrorInit {
  code?: string
  status?: number
  details?: unknown
  requestId?: string | null
  cause?: unknown
  responseMessage?: string | null
}

/**
 * Normalized API failure. UI code must render a catalog key via
 * `friendlyErrorKey`; the server `message` is never logged and is only kept
 * for diagnostics in development.
 */
export class ApiError extends Error {
  readonly code: string
  readonly status: number
  readonly details?: unknown
  readonly requestId?: string | null
  /** Envelope-provided, already-localized server message (null on network bubbles). */
  readonly responseMessage: string | null

  constructor(
    code: string,
    message: string,
    {
      status = 0,
      details,
      requestId = null,
      cause,
      responseMessage = null,
    }: ApiErrorInit = {},
  ) {
    super(message, { cause })
    this.name = 'ApiError'
    this.code = code
    this.status = status
    this.details = details
    this.requestId = requestId
    this.responseMessage = responseMessage
  }

  get isUnauthorized(): boolean {
    return this.code === 'UNAUTHORIZED' || this.status === 401
  }

  static fromEnvelope(
    envelope: ApiErrorEnvelope | null,
    status: number,
    requestId: string | null = null,
  ): ApiError {
    const code = envelope?.error?.code ?? 'UNKNOWN'
    const message = envelope?.error?.message ?? `HTTP ${status}`
    return new ApiError(code, message, {
      status,
      details: envelope?.error?.details,
      requestId,
      responseMessage: typeof envelope?.error?.message === 'string' && envelope.error.message.trim() !== ''
        ? envelope.error.message
        : null,
    })
  }
}
